import { CommentOutlined, SelectOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../utils/ClassNamesUtils';
import { OscarChatSummary } from '../../types/AIAgentTypes';

interface OscarChatListProps {
	chats: OscarChatSummary[];
	/** Id of the chat currently open. */
	activeChatId?: string;
	onSelectChat?: (chatId: string) => void;
	onNewChat?: () => void;
	/** Shuts every control in the rail, e.g. while a conversation is switching. */
	disabled?: boolean;
	className?: string;
}

/**
 * Figma node 14998:125459 — the 380px rail in fullscreen Oscar.
 *
 * Per the design annotations: titles are AI-generated and truncate after two
 * lines, and the context link opens that record in a new tab (in the default,
 * non-fullscreen agent mode) rather than navigating the rail.
 */
export const OscarChatList = ({
	chats,
	activeChatId,
	onSelectChat,
	onNewChat,
	disabled,
	className,
}: OscarChatListProps) => (
	<div className={cn('flex h-full w-[380px] shrink-0 flex-col gap-2.5 py-m', className)}>
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
			<div className="flex w-full shrink-0 items-center gap-2xs px-s py-[5px] text-character-secondary">
				<CommentOutlined className="text-[12px]" />
				<span className="footnoteregular whitespace-nowrap">RECENT CHATS</span>
			</div>
			{chats.map((chat) => (
				<div
					key={chat.id}
					className={cn(
						'flex w-full flex-col gap-2xs border-0 border-b border-solid border-grey-4 px-s py-xs',
						chat.id === activeChatId && 'bg-grey-3'
					)}
				>
					<button
						type="button"
						onClick={onSelectChat ? () => onSelectChat(chat.id) : undefined}
						disabled={disabled}
						title={chat.title}
						// Two-line clamp per the design annotation.
						className={cn(
							'bodyregular m-0 line-clamp-2 w-full border-none bg-transparent p-0 text-left',
							disabled
								? 'cursor-default text-character-disabled'
								: 'cursor-pointer text-character-primary'
						)}
					>
						{chat.title}
					</button>
					{chat.contextLabel ? (
						<span className="flex items-center gap-2xs text-character-secondary">
							<span className="footnoteregular whitespace-nowrap">{chat.contextLabel}</span>
							{chat.contextHref ? (
								<Link
									to={chat.contextHref}
									target="_blank"
									rel="noreferrer"
									aria-label={`Open ${chat.contextLabel} in a new tab`}
									className="flex items-center text-character-secondary hover:text-character-primary"
								>
									<SelectOutlined className="text-[10px]" />
								</Link>
							) : null}
						</span>
					) : null}
				</div>
			))}
		</div>
		<div className="flex w-full shrink-0 flex-col px-m">
			<Button block size="large" onClick={onNewChat} disabled={disabled}>
				+ New chat
			</Button>
		</div>
	</div>
);

export default OscarChatList;
